import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import type { Equipment, MaintenanceInterval, MaintenanceIntervalFormData, IntervalType } from '../../types/equipment';
import { Clock, Plus, Trash2, CheckCircle, AlertTriangle, Edit2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { toNum, toNumOr } from '@/utils/numeric';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nextDueText(mi: MaintenanceInterval, eq: Equipment): { text: string; danger: boolean } {
  const dueHours = toNum(mi.next_due_hours);
  const dueKm = toNum(mi.next_due_km);
  if (mi.interval_type === 'hours' && dueHours != null) {
    const rem = dueHours - toNumOr(eq.current_hours);
    if (rem <= 0) return { text: `Overdue by ${Math.abs(rem).toFixed(1)} hrs`, danger: true };
    return { text: `Due at ${dueHours} hrs (in ${rem.toFixed(1)} hrs)`, danger: false };
  }
  if (mi.interval_type === 'km' && dueKm != null) {
    const rem = dueKm - toNumOr(eq.current_km);
    if (rem <= 0) return { text: `Overdue by ${Math.abs(rem).toFixed(0)} km`, danger: true };
    return { text: `Due at ${dueKm.toLocaleString()} km (in ${rem.toFixed(0)} km)`, danger: false };
  }
  if (mi.interval_type === 'days' && mi.next_due_date) {
    const days = Math.ceil((new Date(mi.next_due_date).getTime() - Date.now()) / 86400000);
    if (days <= 0) return { text: `Overdue by ${Math.abs(days)} days`, danger: true };
    return { text: `Due ${mi.next_due_date} (in ${days} days)`, danger: false };
  }
  return { text: 'Not yet scheduled', danger: false };
}

// ─── Interval Form ────────────────────────────────────────────────────────────

const EMPTY_FORM: MaintenanceIntervalFormData = {
  name: '',
  interval_type: 'hours',
  interval_value: 100,
  alert_before: 5,
  last_done_hours: null,
  last_done_km: null,
  last_done_date: null,
};

interface IntervalFormProps {
  initial?: MaintenanceIntervalFormData;
  meterType: Equipment['meter_type'];
  onSave: (data: MaintenanceIntervalFormData) => Promise<void>;
  onCancel: () => void;
}

function IntervalForm({ initial, meterType, onSave, onCancel }: IntervalFormProps) {
  const [form, setForm] = useState<MaintenanceIntervalFormData>(initial ?? EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof MaintenanceIntervalFormData, v: unknown) =>
    setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.interval_value || form.interval_value <= 0) { setError('Interval value must be > 0'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  const intervalTypeOptions: { value: IntervalType; label: string }[] = [
    ...(meterType === 'hours' || meterType === 'both' ? [{ value: 'hours' as IntervalType, label: 'Hours' }] : []),
    ...(meterType === 'km'    || meterType === 'both' ? [{ value: 'km'    as IntervalType, label: 'Km' }] : []),
    { value: 'days', label: 'Days' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />{error}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Task name *</label>
        <input
          type="text"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="e.g. Change engine oil"
          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Interval type *</label>
          <select
            value={form.interval_type}
            onChange={e => set('interval_type', e.target.value as IntervalType)}
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {intervalTypeOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">
            Every * ({form.interval_type === 'days' ? 'days' : form.interval_type})
          </label>
          <input
            type="number" min="1" step="1"
            value={form.interval_value}
            onChange={e => set('interval_value', parseFloat(e.target.value) || 0)}
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">
          Alert before ({form.interval_type === 'days' ? 'days' : form.interval_type})
        </label>
        <input
          type="number" min="0" step="1"
          value={form.alert_before ?? 5}
          onChange={e => set('alert_before', parseFloat(e.target.value) || 0)}
          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <p className="text-xs text-neutral-400 mt-0.5">Shows in pre-start checklist this many {form.interval_type === 'days' ? 'days' : form.interval_type} before due</p>
      </div>

      {/* Optional: last done reading */}
      {form.interval_type === 'hours' && (
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Last done at (hours) — optional</label>
          <input
            type="number" min="0" step="0.1"
            value={form.last_done_hours ?? ''}
            onChange={e => set('last_done_hours', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Leave blank to start from current"
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      )}
      {form.interval_type === 'km' && (
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Last done at (km) — optional</label>
          <input
            type="number" min="0" step="1"
            value={form.last_done_km ?? ''}
            onChange={e => set('last_done_km', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Leave blank to start from current"
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      )}
      {form.interval_type === 'days' && (
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Last done date — optional</label>
          <input
            type="date"
            value={form.last_done_date ?? ''}
            onChange={e => set('last_done_date', e.target.value || null)}
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button isLoading={saving} type="submit" className="flex-1">
          <CheckCircle className="h-4 w-4 mr-1.5" />
          Save
        </Button>
      </div>
    </form>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function MaintenanceManagement() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eqId = parseInt(id!);

  useModuleLicense('vehicles');

  const [equipment, setEquipment]     = useState<Equipment | null>(null);
  const [intervals, setIntervals]     = useState<MaintenanceInterval[]>([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState<number | null>(null);
  const [deletingId, setDeletingId]   = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.getMaintenanceIntervals(eqId),
    ]).then(([eqRes, miRes]) => {
      if (eqRes.success && eqRes.data) setEquipment(eqRes.data);
      if (miRes.success && miRes.data) setIntervals(miRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId]);

  async function handleCreate(data: MaintenanceIntervalFormData) {
    const res = await equipmentService.createMaintenanceInterval(eqId, data);
    if (res.success && res.data) {
      setIntervals(prev => [...prev, res.data!]);
      setShowForm(false);
    }
  }

  async function handleUpdate(intervalId: number, data: MaintenanceIntervalFormData) {
    const res = await equipmentService.updateMaintenanceInterval(intervalId, data);
    if (res.success && res.data) {
      setIntervals(prev => prev.map(mi => mi.id === intervalId ? res.data! : mi));
      setEditingId(null);
    }
  }

  async function handleDelete(intervalId: number) {
    if (!confirm('Delete this maintenance interval?')) return;
    setDeletingId(intervalId);
    try {
      await equipmentService.deleteMaintenanceInterval(intervalId);
      setIntervals(prev => prev.filter(mi => mi.id !== intervalId));
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  }

  if (loading || !equipment) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Maintenance Intervals" />
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  const activeIntervals   = intervals.filter(mi => mi.is_active);
  const inactiveIntervals = intervals.filter(mi => !mi.is_active);

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Maintenance Intervals"
        subtitle={equipment.name}
        actionButtons={
          <Button size="sm" onClick={() => { setShowForm(true); setEditingId(null); }}>
            <Plus className="h-4 w-4 mr-1" />
            Add Interval
          </Button>
        }
      />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Add form */}
        {showForm && (
          <Card>
            <CardContent className="pt-4">
              <p className="font-semibold text-neutral-800 mb-3">New Maintenance Interval</p>
              <IntervalForm
                meterType={equipment.meter_type}
                onSave={handleCreate}
                onCancel={() => setShowForm(false)}
              />
            </CardContent>
          </Card>
        )}

        {/* Active intervals */}
        {activeIntervals.length === 0 && !showForm ? (
          <div className="text-center py-12">
            <Clock className="h-10 w-10 text-neutral-200 mx-auto mb-3" />
            <p className="text-neutral-500 mb-1">No maintenance intervals yet</p>
            <p className="text-sm text-neutral-400 mb-4">Add intervals to track scheduled maintenance and generate pre-start checklist items.</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add First Interval
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {activeIntervals.map(mi => {
              const due = nextDueText(mi, equipment);
              const isEditing = editingId === mi.id;
              return (
                <Card key={mi.id}>
                  <CardContent className="pt-4">
                    {isEditing ? (
                      <>
                        <p className="font-semibold text-neutral-800 mb-3">Edit: {mi.name}</p>
                        <IntervalForm
                          initial={{
                            name: mi.name,
                            interval_type: mi.interval_type,
                            interval_value: toNumOr(mi.interval_value),
                            alert_before: toNumOr(mi.alert_before),
                            last_done_hours: toNum(mi.last_done_hours),
                            last_done_km: toNum(mi.last_done_km),
                            last_done_date: mi.last_done_date ?? null,
                          }}
                          meterType={equipment.meter_type}
                          onSave={data => handleUpdate(mi.id, data)}
                          onCancel={() => setEditingId(null)}
                        />
                      </>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-neutral-800">{mi.name}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            Every {mi.interval_value} {mi.interval_type} · alert {mi.alert_before} {mi.interval_type} before
                          </p>
                          <p className={cn('text-xs mt-1 font-medium', due.danger ? 'text-red-600' : 'text-neutral-500')}>
                            {due.text}
                          </p>
                          {mi.last_done_date && (
                            <p className="text-xs text-neutral-400 mt-0.5">Last done: {mi.last_done_date}</p>
                          )}
                          {mi.last_done_hours != null && (
                            <p className="text-xs text-neutral-400 mt-0.5">Last done at: {mi.last_done_hours} hrs</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setEditingId(mi.id)}
                            className="p-1.5 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(mi.id)}
                            disabled={deletingId === mi.id}
                            className="p-1.5 text-neutral-400 hover:text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Inactive/archived */}
        {inactiveIntervals.length > 0 && (
          <div>
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-2 px-1">Inactive</p>
            <div className="space-y-2">
              {inactiveIntervals.map(mi => (
                <div key={mi.id} className="bg-white border border-neutral-100 rounded-xl px-4 py-3 flex items-center justify-between opacity-60">
                  <div>
                    <p className="text-sm font-medium text-neutral-600 line-through">{mi.name}</p>
                    <p className="text-xs text-neutral-400">Every {mi.interval_value} {mi.interval_type}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(mi.id)}
                    disabled={deletingId === mi.id}
                    className="p-1.5 text-neutral-300 hover:text-red-500 rounded-lg"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button variant="outline" className="w-full" onClick={() => navigate(`/equipment/${eqId}`)}>
          Back to {equipment.name}
        </Button>

      </div>
    </div>
  );
}
